/**
 * ArtistChip — an Archive artist with its tag: UNCLAIMED (accent), ARCHIVE LEGEND (muted),
 * ON SOUNDDAO (links to /creator/:id), plus NEW for debut acts and FOUNDER'S PICK for
 * founder-curated artists (migration 043; same muted style as ARCHIVE LEGEND).
 */

import React, { useState } from 'react';
import { nominate, useNominations } from '../../lib/nominationsStore';
import { Link } from 'react-router-dom';
import { useRates, sound } from '../../lib/rates';
import { track } from '../../lib/analytics';
import FounderPickTag from './FounderPickTag';

const TAGS = {
  unclaimed:   { label: 'UNCLAIMED',      cls: 'ax-tag--unclaimed' },
  legend:      { label: 'ARCHIVE LEGEND', cls: 'ax-tag--legend' },
  on_sounddao: { label: 'ON SOUNDDAO ›',  cls: 'ax-tag--on' },
};

export default function ArtistChip({ artist }) {
  const tag = TAGS[artist.tag] || TAGS.unclaimed;
  const body = (
    <>
      {artist.displayName}
      {artist.isNew && artist.tag === 'unclaimed' && <span className="ax-tag ax-tag--new">NEW</span>}
      <span className={`ax-tag ${tag.cls}`}>{tag.label}</span>
      {artist.founderPick && <FounderPickTag />}
    </>
  );
  if (artist.tag === 'on_sounddao' && artist.creatorUserId) {
    return <Link className="ax-chip" to={`/creator/${artist.creatorUserId}`}>{body}</Link>;
  }
  return <span className="ax-chip">{body}</span>;
}

/**
 * Nominate (Prompt 6): UNCLAIMED artists only. The server re-checks activity, allows one
 * nomination per artist and 5 per rolling week, and pays only if the band later VERIFIES a
 * claim. The confirmation quotes tonight's LIVE amounts (GET /api/rewards/rates), never a base
 * rate: first nominator → recruit_creator, nominators 2–5 → archive_scout.
 */
export function NominateButton({ artists }) {
  const nominable = (artists || []).filter((a) => a.tag === 'unclaimed');
  const noms = useNominations();
  const rates = useRates();
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  if (!nominable.length) return null;

  const atLimit = noms.usedThisWeek >= noms.limit;
  const nextSlot = noms.nextSlotAt ? new Date(noms.nextSlotAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : null;

  const go = async (a) => {
    setBusy(a.id);
    setMsg(null);
    try {
      const r = await nominate(a.id);
      track('artist_nominated');   // no artist id or name
      let copied = false;
      try { await navigator.clipboard.writeText(r.claimUrl); copied = true; } catch { /* clipboard unavailable */ }
      setMsg({ ok: true, text: `Nominated ${a.displayName}. If they claim and verify their profile, the first nominator earns ${sound(rates, 'recruit_creator')} and nominators 2–5 earn ${sound(rates, 'archive_scout')} (needs a confirmed email and one earlier post, share or scout).${copied ? ' Claim link copied — send it to the band.' : ''}`, url: r.claimUrl });
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {nominable.map((a) => {
        const done = noms.artistIds.has(a.id);
        const label = done ? '✓ Nominated'
          : busy === a.id ? '…'
            : `Nominate${nominable.length > 1 ? ` ${a.displayName}` : ''}`;
        return (
          <button
            key={a.id}
            type="button"
            className={`ax-btn ax-btn--sm ${done ? '' : 'ax-btn--nominate'}`}
            disabled={done || busy !== null || (atLimit && !done) || !noms.loaded}
            title={atLimit && !done ? `You've used your ${noms.limit} nominations this week${nextSlot ? ` — next one frees up ${nextSlot}` : ''}.` : 'Nominate this artist to join SoundDAO'}
            onClick={() => go(a)}
          >
            {label}
          </button>
        );
      })}
      {atLimit && nominable.some((a) => !noms.artistIds.has(a.id)) && (
        <span className="ax-meta" style={{ width: '100%' }}>{noms.limit} of {noms.limit} nominations used this week{nextSlot ? ` · next frees up ${nextSlot}` : ''}.</span>
      )}
      {msg && (
        <span className="ax-meta" style={{ width: '100%', color: msg.ok ? '#64ffb0' : '#ff9d9d' }}>
          {msg.text}{msg.url && <> <a href={msg.url} style={{ color: '#a5b4fc' }}>Claim page ↗</a></>}
        </span>
      )}
    </>
  );
}
